import { useEffect, useRef, useState } from 'react';
import { Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useNavigate } from 'react-router-dom';
import { useAppContext } from '@/context/AppContext';
import type { HoldMultiDayResult, Stage, VenueList } from 'api-client';
import { createDateUTC, formatDateLocal, isPastDate } from '@/utils/dateUtils';
import { readHoldCreationError } from '@/bookings/utils/holdCreation';
import { getVenueStageSelection } from '@/utils/selectionDefaults';
import { HoldCreationResult } from './HoldCreationResult';
import { HoldQuickCreateFields, type HoldQuickCreateDraft } from './HoldQuickCreateFields';

interface HoldQuickCreateModalProps {
  open: boolean;
  onClose: () => void;
  selectedDates: Date[];
  onSuccess?: () => void;
}

const emptyDraft: HoldQuickCreateDraft = { title: '', venueId: '', stageId: '', notes: '' };

export function HoldQuickCreateModal({ open, onClose, selectedDates, onSuccess }: HoldQuickCreateModalProps) {
  const navigate = useNavigate();
  const { apiClients, team } = useAppContext();
  const [venues, setVenues] = useState<VenueList[]>([]);
  const [stages, setStages] = useState<Stage[]>([]);
  const [loadingChoices, setLoadingChoices] = useState(false);
  const [pending, setPending] = useState(false);
  const pendingRef = useRef(false);
  const wasOpen = useRef(false);
  const choicesRequest = useRef(0);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<HoldMultiDayResult | null>(null);
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [draft, setDraft] = useState<HoldQuickCreateDraft>(emptyDraft);
  const hasSavedHolds = Boolean(result && result.createdCount > 0);

  useEffect(() => {
    const opening = open && !wasOpen.current;
    wasOpen.current = open;
    if (!opening) return;
    const sorted = [...selectedDates].sort((a, b) => a.getTime() - b.getTime());
    setStartDate(sorted[0] || null);
    setEndDate(sorted[sorted.length - 1] || null);
    setLoadingChoices(true);
    const request = ++choicesRequest.current;
    void Promise.all([apiClients.venues.venuesList(), apiClients.stages.stagesList()])
      .then(([venueResponse, stageResponse]) => {
        if (request !== choicesRequest.current || !wasOpen.current) return;
        const availableVenues = venueResponse.results || [];
        const availableStages = stageResponse.results || [];
        setVenues(availableVenues);
        setStages(availableStages);
        setDraft((value) => {
          const selection = getVenueStageSelection(availableVenues, availableStages, {
            venue: value.venueId, stage: value.stageId,
          });
          return { ...value, venueId: selection.venue, stageId: selection.stage };
        });
      })
      .catch(() => {
        if (request === choicesRequest.current && wasOpen.current) {
          setError('Failed to load venues and stages. Close and reopen to try again.');
        }
      })
      .finally(() => {
        if (request === choicesRequest.current) setLoadingChoices(false);
      });
  }, [open, selectedDates, apiClients]);

  useEffect(() => () => { choicesRequest.current += 1; }, []);

  const close = () => {
    if (pendingRef.current) return;
    choicesRequest.current += 1;
    setDraft(emptyDraft);
    setResult(null);
    setError(null);
    setStartDate(null);
    setEndDate(null);
    onClose();
  };

  const submit = async () => {
    if (pendingRef.current || hasSavedHolds) return;
    if (!draft.title.trim() || !draft.venueId || !draft.stageId) {
      setError('Enter a show title, venue and stage.');
      return;
    }
    if (!startDate || !endDate || Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime()) || endDate < startDate) {
      setError('Choose a valid start and end date.');
      return;
    }
    if (isPastDate(startDate)) {
      setError('Cannot create holds for past dates. Choose today or a future date.');
      return;
    }
    pendingRef.current = true;
    setPending(true);
    setError(null);
    setResult(null);
    try {
      const promoterTeam = team;
      if (!promoterTeam) {
        setError('Select a team before creating holds.');
        return;
      }
      const saved = await apiClients.holds.holdsCreateMultiDay({
        holdsCreateMultiDayRequest: {
          title: draft.title.trim(), stage: draft.stageId,
          startDate: createDateUTC(startDate), endDate: createDateUTC(endDate),
          promoterTeam: promoterTeam.id, notes: draft.notes,
        },
      });
      setResult(saved);
      if (saved.createdCount > 0) onSuccess?.();
      if (saved.createdCount > 0 && saved.skippedCount === 0) {
        pendingRef.current = false;
        close();
      }
    } catch (failure: unknown) {
      const rejected = await readHoldCreationError(failure);
      setResult(rejected.result);
      if (!rejected.result) setError(rejected.message);
    } finally {
      pendingRef.current = false;
      setPending(false);
    }
  };

  const openFullForm = () => {
    if (!startDate || !endDate) return;
    const params = new URLSearchParams({
      startDate: formatDateLocal(startDate), endDate: formatDateLocal(endDate),
      title: draft.title, stage: draft.stageId, notes: draft.notes,
    });
    navigate(`/bookings/new?${params.toString()}`);
    close();
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Dialog open={open} onClose={pending ? undefined : close} maxWidth="sm" fullWidth aria-labelledby="create-hold-title">
        <DialogTitle id="create-hold-title">Create Hold</DialogTitle>
        <DialogContent>
          <Stack spacing={2.5} sx={{ mt: 1 }}>
            {result && <HoldCreationResult result={result} />}
            {error && <Alert severity="error">{error}</Alert>}
            {loadingChoices && <Alert severity="info">Loading venues and stages…</Alert>}
            <HoldQuickCreateFields
              value={draft} onChange={setDraft} startDate={startDate} endDate={endDate}
              onStartDate={setStartDate} onEndDate={setEndDate} venues={venues} stages={stages}
              disabled={pending || loadingChoices || hasSavedHolds}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, flexWrap: 'wrap', gap: 1 }}>
          <Button onClick={close} disabled={pending}>{hasSavedHolds ? 'Close' : 'Cancel'}</Button>
          {hasSavedHolds && result?.show ? (
            <Button onClick={() => { navigate(`/shows/${result.show!.id}`); close(); }} variant="contained">View Show</Button>
          ) : (
            <>
              <Button onClick={openFullForm} disabled={pending || !startDate || !endDate}>More Options</Button>
              <Button onClick={() => void submit()} variant="contained" loading={pending} loadingPosition="start"
                disabled={pending || loadingChoices || !draft.title.trim() || !draft.stageId}>
                Create Hold
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </LocalizationProvider>
  );
}
